import React from 'react';
import first from 'lodash/first';
import classNames from 'classnames';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Rating from '@mui/material/Rating';
import IconButton from '@mui/material/IconButton';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { grey, green, yellow } from '@mui/material/colors';

import { TesterStatusInCycle } from '../../../../types/TesterStatusInCycle';
import { CycleStatus } from '../../../../types/CycleStatus';

const useStyles = makeStyles(() => ({
    root: {
      minWidth: 210,
      boxShadow: 'none',
      textAlign: 'center',
      padding: '2px 45px',
      border: `1px solid ${grey[200]}`,
      position: 'relative',
    },
    favorite: {
        position: 'absolute',
        top: 4,
        right: 4,
    },
    favoriteOn: {
        color: '#FFB400',
    },
    reviewButton: {
        marginTop: 15,
    },
    cardCotent: {
        '&:last-child': {
            paddingBottom: 10,
        },
    },
    avatar: {
        fontSize: 15,
        margin: '0 auto 20px',
        backgroundColor: '#00BCD7',
    },
    testerName: {
        maxWidth: 150,
        marginBottom: 15,
    },
    chip: {
        borderRadius: 4,
        marginBottom: 5,
        backgroundColor: grey[200],
    },
    chipCompleted: {
        color: green[500],
        background: green[50],
    },
    chipInProgress: {
        color: yellow[800],
        background: '#FFF8EE',
    },
    smSpaceBottom: {
        marginBottom: 15,
    },
}));

interface ITesterCardProps {
    firstName?: string;
    lastName?: string;
    country?: string;
    status?: TesterStatusInCycle;
    avatarUrl?: string;
    className?: string;
    cycleStatus?: CycleStatus;
    isFavorite?: boolean;
    readOnly?: boolean;
    onToggleFavorite?: () => void;
    onReview?: () => void;
    reviewRating?: number;
    onViewReview?: () => void;
};

/* Reviewing marks the tester Finished in wt, so Finished means "already reviewed".
   Only testers who said they finished (AwaitingReview) can be reviewed: reviewing an
   in-progress tester would mark them Finished and get them paid at cycle end. */
const reviewState = (status: TesterStatusInCycle) => {
    if (status === TesterStatusInCycle.Finished) return 'reviewed';
    if (status === TesterStatusInCycle.AwaitingReview) return 'open';
    return 'notYet';
};

const TesterCard: React.FC<ITesterCardProps> = ({ 
    firstName = '',
    lastName = '',
    country = '',
    status = TesterStatusInCycle.Approved,
    cycleStatus,
    avatarUrl = null,
    className = '',
    isFavorite = false,
    readOnly = false,
    onToggleFavorite,
    onReview,
    reviewRating,
    onViewReview,
}) => {
    const classes = useStyles();    
    
    const testerStatus = 
        [TesterStatusInCycle.Finished, TesterStatusInCycle.AwaitingReview].includes( status ) ? 'Complete' 
                                                                                        : ( ( cycleStatus === CycleStatus.Completed ) ? 'Not finished' : 'In progress') ;    
    return (
        <Card className={classNames(classes.root, className)}>
            {onToggleFavorite && (
                <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                    <span className={classes.favorite}>
                        <IconButton
                            size="small"
                            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                            aria-pressed={isFavorite}
                            onClick={onToggleFavorite}
                            disabled={readOnly}
                            className={classNames({ [classes.favoriteOn]: isFavorite })}
                        >
                            {isFavorite ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
            <CardContent className={classes.cardCotent}>
                {avatarUrl ? (
                    <Avatar className={classes.avatar} src={avatarUrl} />
                ) : (
                    <Avatar className={classes.avatar}>
                        {first(firstName)}{first(lastName)}
                    </Avatar>
                )}
                

                <Typography 
                    gutterBottom 
                    component="h2" 
                    variant="body1" 
                    color="textSecondary" 
                    className={classes.testerName}
                >
                    {firstName} {lastName}
                </Typography>

                <div className={classes.smSpaceBottom}>
                    <Chip label={country} className={classes.chip} />
                </div>

                <div>
                    {cycleStatus !== CycleStatus.Completed &&
                    <Chip 
                        label={testerStatus} 
                        className={classNames(classes.chip, {
                            [classes.chipCompleted]: testerStatus === 'Complete',
                            [classes.chipInProgress]: (testerStatus === 'In progress' || testerStatus === 'Not finished'),
                        })}
                    />}
                </div>

                {onReview && (() => {
                    const state = reviewState(status);
                    if (reviewRating && onViewReview) {
                        return (
                            <div className={classes.reviewButton}>
                                <Rating value={reviewRating} readOnly size="small" />
                                <div>
                                    <Button size="small" onClick={onViewReview}>View review</Button>
                                </div>
                            </div>
                        );
                    }
                    if (state === 'reviewed') {
                        return <Button size="small" disabled className={classes.reviewButton}>Reviewed</Button>;
                    }
                    const tip = state === 'notYet' ? 'Available once the tester finishes' : '';
                    return (
                        <Tooltip title={tip}>
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    className={classes.reviewButton}
                                    disabled={readOnly || state === 'notYet'}
                                    onClick={onReview}
                                >
                                    Rate tester
                                </Button>
                            </span>
                        </Tooltip>
                    );
                })()}
            </CardContent>
        </Card>
    );
};

export default TesterCard;